import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Calendar,
  MapPin,
  Users,
  Wallet,
  Loader2,
  Layers,
  Copy,
  Edit,
  BarChart3
} from 'lucide-react';

const CampaignDetail = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addEventOpen, setAddEventOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const [newEvent, setNewEvent] = useState({
    event_name: '',
    event_location: '',
    event_description: '',
    expected_attendees: 100,
    start_date: '',
    end_date: ''
  });

  const fetchCampaign = useCallback(async () => {
    try {
      const response = await api.get(`/campaigns/${campaignId}`);
      setCampaign(response.data);
    } catch (error) {
      toast.error('Failed to load campaign');
      navigate('/admin/events');
    } finally {
      setLoading(false);
    }
  }, [campaignId, navigate]);

  useEffect(() => {
    fetchCampaign();
  }, [fetchCampaign]);

  const handleAddEvent = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const eventData = {
        event_name: newEvent.event_name,
        event_location: newEvent.event_location,
        event_description: newEvent.event_description,
        expected_attendees: parseInt(newEvent.expected_attendees),
        event_dates: [{
          start_date: newEvent.start_date,
          end_date: newEvent.end_date || newEvent.start_date
        }]
      };
      await api.post(`/campaigns/${campaignId}/events`, eventData);
      toast.success('Event added to campaign');
      setAddEventOpen(false);
      setNewEvent({
        event_name: '', event_location: '', event_description: '',
        expected_attendees: 100, start_date: '', end_date: ''
      });
      fetchCampaign();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add event');
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBA';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
  };

  if (loading) {
    return (
      <AdminLayout title="Campaign">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!campaign) return null;

  const events = campaign.events || [];
  const walletSummary = campaign.wallet_summary;

  return (
    <AdminLayout title={campaign.campaign_name}>
      {/* Back button */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/admin/events')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Events
        </Button>
      </div>

      {/* Campaign Info Card */}
      <Card className="mb-6">
        <div className="h-2" style={{ backgroundColor: campaign.branding_color || '#3B82F6' }} />
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
            <div>
              <h1 className="text-2xl font-bold">{campaign.campaign_name}</h1>
              <div className="flex items-center gap-3 mt-2">
                <button
                  onClick={() => copyToClipboard(campaign.campaign_code)}
                  className="flex items-center gap-1 text-sm bg-gray-100 px-3 py-1 rounded-full hover:bg-gray-200 transition-colors"
                >
                  <span className="font-mono font-medium">{campaign.campaign_code}</span>
                  <Copy className="h-3 w-3" />
                </button>
                {campaign.wallet_enabled && (
                  <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                    <Wallet className="h-3 w-3" /> Wallet Enabled
                  </span>
                )}
                <span className={`text-xs px-2 py-1 rounded-full ${
                  campaign.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {campaign.status}
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setAddEventOpen(true)}>
                <Plus className="h-4 w-4 mr-2" /> Add Event
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Wallet Summary (if enabled) */}
      {campaign.wallet_enabled && walletSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Total Wallets</div>
              <div className="text-2xl font-bold">{walletSummary.total_wallets || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Total Topped Up</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(walletSummary.total_topped_up)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Total Spent</div>
              <div className="text-2xl font-bold text-orange-600">{formatCurrency(walletSummary.total_spent)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Outstanding Balance</div>
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(walletSummary.total_balance)}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Events Grid */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Events ({events.length})</h2>
      </div>

      {events.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar className="h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No events yet</h3>
            <p className="text-gray-500 mb-4">Add events to this campaign</p>
            <Button onClick={() => setAddEventOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> Add Event
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event) => (
            <Card key={event.event_id} className="overflow-hidden hover:shadow-lg transition-shadow">
              <div className="h-2" style={{ backgroundColor: event.branding_color || campaign.branding_color || '#3B82F6' }} />
              <CardContent className="p-5">
                <h3 className="font-semibold text-lg line-clamp-1 mb-3">{event.event_name}</h3>
                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    <span className="line-clamp-1">{event.event_location || 'TBA'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span>
                      {event.event_dates?.[0]?.start_date
                        ? formatDate(event.event_dates[0].start_date)
                        : 'TBA'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    <span>{event.actual_attendees || 0} / {event.expected_attendees || 0} attendees</span>
                  </div>
                </div>
                <Link to={`/admin/events/${event.event_id}`}>
                  <Button variant="outline" className="w-full">Manage Event</Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Event Dialog */}
      <Dialog open={addEventOpen} onOpenChange={setAddEventOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Event to Campaign</DialogTitle>
            <DialogDescription>Create a new event under {campaign.campaign_name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddEvent} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="event_name">Event Name *</Label>
              <Input
                id="event_name"
                value={newEvent.event_name}
                onChange={(e) => setNewEvent({...newEvent, event_name: e.target.value})}
                placeholder="Day 2 - Workshop"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="event_location">Location *</Label>
              <Input
                id="event_location"
                value={newEvent.event_location}
                onChange={(e) => setNewEvent({...newEvent, event_location: e.target.value})}
                placeholder="Convention Center, Mumbai"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={newEvent.start_date}
                  onChange={(e) => setNewEvent({...newEvent, start_date: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={newEvent.end_date}
                  onChange={(e) => setNewEvent({...newEvent, end_date: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Expected Attendees</Label>
              <Input
                type="number"
                value={newEvent.expected_attendees}
                onChange={(e) => setNewEvent({...newEvent, expected_attendees: e.target.value})}
                min="1" max="1000000"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={newEvent.event_description}
                onChange={(e) => setNewEvent({...newEvent, event_description: e.target.value})}
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setAddEventOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={creating}>
                {creating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Adding...</> : 'Add Event'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default CampaignDetail;
